import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Sparkles, Eye, EyeOff, Check, Zap, MessageSquare, Shield, Globe } from "lucide-react";
import SocialLoginButtons from "../components/SocialLoginButtons";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const hasLength = password.length >= 8;
  const hasUpper = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters");
    if (!hasUpper) return setError("Password must contain at least one uppercase letter");
    if (!hasNumber) return setError("Password must contain at least one number");
    setLoading(true);
    try {
      // AuthContext.register expects (email, password, name) — the previous
      // call had its arguments in the wrong order, so the email field was
      // submitted as name and vice versa.
      const result = await register(email, password, name);
      if (!result?.success) {
        const msg = result?.error || "Registration failed";
        setError(msg);
        toast.error(msg);
        return;
      }
      toast.success("Account created!");
      navigate("/profile/setup");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Branding Panel — desktop only */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute inset-0">
          <img src="https://static.prod-images.emergentagent.com/jobs/69522626-12bf-4c16-9d63-ce7a21ed458c/images/b70fad5fe4f0879dda4d9a8a8ee180a9cc47ce95cc3ab73e3b65746127284758.jpeg" alt="" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a0716]/95 via-[#140d33]/85 to-[#160a2e]/90" />
          <div className="absolute inset-0 bg-aurora animate-aurora" />
        </div>
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"}} />
        <div className="absolute top-32 left-16 w-24 h-24 bg-white/5 rounded-2xl rotate-12 animate-float" style={{animationDelay: "0.5s"}} />
        <div className="absolute bottom-20 right-20 w-16 h-16 bg-white/5 rounded-xl -rotate-12 animate-float" style={{animationDelay: "2.5s"}} />

        <div className="relative z-10">
          <div className="flex items-center gap-2.5 mb-12">
            <div className="w-9 h-9 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Sparkles className="w-4.5 h-4.5 text-white" />
            </div>
            <span className="font-outfit text-xl font-semibold text-white">App-Mi.ai</span>
          </div>
          <h2 className="font-outfit text-3xl lg:text-4xl font-light text-white leading-tight mb-4">
            Create Your<br /><span className="font-medium">AI Assistant</span>
          </h2>
          <p className="text-base text-purple-200 leading-relaxed max-w-md">
            Join thousands building personalized AI experiences. Get started free in seconds.
          </p>
        </div>

        <div className="relative z-10">
          <p className="text-xs uppercase tracking-wider text-purple-300 font-semibold mb-4">What you get</p>
          <div className="space-y-3">
            {[
              { icon: Zap, label: "8 free AI models — all included" },
              { icon: MessageSquare, label: "Voice & text chat with personalities" },
              { icon: Globe, label: "Your own custom AI URL" },
              { icon: Shield, label: "Private & encrypted conversations" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <f.icon className="w-4 h-4 text-purple-200" />
                </div>
                <span className="text-sm text-purple-100">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-16 relative bg-aurora">
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="text-center mb-8 lg:text-left">
            <div className="w-14 h-14 mx-auto lg:mx-0 mb-4 rounded-2xl bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center shadow-glow-sm">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <h1 className="font-outfit text-2xl font-semibold text-foreground" data-testid="register-heading">Create your account</h1>
            <p className="text-sm text-muted-foreground mt-1">Get started with App-Mi.ai in seconds</p>
          </div>

          <div className="glass-panel rounded-2xl shadow-card-dark p-8">
            {/* Social Signup Buttons */}
            <SocialLoginButtons />
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-muted" />
              <span className="text-xs text-muted-foreground/70">or sign up with email</span>
              <div className="flex-1 h-px bg-muted" />
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-sm text-red-400 animate-fade-in" data-testid="register-error">{error}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1.5">Name</label>
                <input data-testid="register-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required className="w-full px-4 py-2.5 bg-muted/40 border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400 transition-colors" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1.5">Email</label>
                <input data-testid="register-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required className="w-full px-4 py-2.5 bg-muted/40 border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400 transition-colors" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1.5">Password</label>
                <div className="relative">
                  <input data-testid="register-password-input" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Create a strong password" required className="w-full px-4 py-2.5 bg-muted/40 border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400 transition-colors pr-10" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 hover:text-muted-foreground bg-transparent border-0 cursor-pointer" data-testid="register-toggle-password">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {password && (
                  <div className="flex gap-3 mt-2">
                    {[
                      { label: "8+ chars", ok: hasLength },
                      { label: "Uppercase", ok: hasUpper },
                      { label: "Number", ok: hasNumber },
                    ].map((r, i) => (
                      <span key={i} className={`flex items-center gap-1 text-[11px] transition-colors duration-300 ${r.ok ? "text-emerald-400" : "text-muted-foreground/70"}`}>
                        <Check className={`w-3 h-3 transition-colors duration-300 ${r.ok ? "text-emerald-500" : "text-muted-foreground/60"}`} /> {r.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-start gap-2">
                <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} className="mt-1 w-4 h-4 rounded border-border text-cyan-400 focus:ring-cyan-400 cursor-pointer" data-testid="agree-checkbox" />
                <label className="text-xs text-muted-foreground">
                  I agree to the <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">Privacy Policy</a> and <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">Terms of Service</a>
                </label>
              </div>
              <Button data-testid="register-submit-btn" type="submit" disabled={loading || !agreedToTerms} className="w-full bg-gradient-to-r from-cyan-400 to-violet-500 hover:from-cyan-400 hover:to-violet-600 text-white h-11 rounded-lg text-sm font-medium shadow-glow-sm hover:-translate-y-0.5 transition-all press-down disabled:opacity-40">
                {loading ? "Creating account..." : "Create Account"}
              </Button>
            </form>
            <p className="text-sm text-center text-muted-foreground mt-6">
              Already have an account?{" "}
              <Link to="/login" className="text-cyan-400 hover:text-cyan-300 font-medium no-underline" data-testid="register-login-link">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
